<script setup>
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useProductsStore } from '@/stores/products'
import { useAuthStore } from '@/stores/auth'
import { categories } from '@/data/products'

const route = useRoute()
const router = useRouter()
const productsStore = useProductsStore()
const authStore = useAuthStore()

// 获取产品数据
const product = computed(() => productsStore.currentProduct)
const loading = computed(() => productsStore.loading)

// 获取分类名称
const categoryName = computed(() => {
  const cat = categories.find(c => c.id === product.value?.category)
  return cat?.name || '产品'
})

// 处理产品不存在的情况
onMounted(async () => {
  const result = await productsStore.fetchProductById(route.params.id)
  if (!result) {
    router.push('/products')
  }
})

// 申请样品
function handleApplySample() {
  if (!authStore.isLoggedIn) {
    router.push({ path: '/login', query: { redirect: route.fullPath } })
    return
  }
  router.push({ path: '/sample/apply', query: { productId: route.params.id } })
}

// 佣金计算
function handleCommissionCalc() {
  router.push({ path: '/commission/calc', query: { productId: route.params.id } })
}

// 处理下载数据手册
function handleDownloadDatasheet() {
  if (product.value?.datasheet) {
    const link = document.createElement('a')
    link.href = product.value.datasheet
    link.download = `${product.value.model}-datasheet.pdf`
    link.click()
  } else {
    alert('数据手册暂未提供')
  }
}
</script>

<template>
  <div v-if="loading" class="detail-page loading-state">
    <div class="container">
      <div class="spinner"></div>
      <p>加载中...</p>
    </div>
  </div>

  <div v-else-if="product" class="detail-page">
    <div class="container">
      <!-- 面包屑导航 -->
      <nav class="breadcrumb">
        <router-link to="/">首页</router-link>
        <span> &gt; </span>
        <router-link to="/products">产品中心</router-link>
        <span> &gt; </span>
        <router-link :to="`/products?category=${product.category}`">{{ categoryName }}</router-link>
        <span> &gt; </span>
        <span>{{ product.name }}</span>
      </nav>

      <!-- 主内容区：左右布局 -->
      <div class="detail-content">
        <!-- 左侧：产品图片 -->
        <div class="image-section">
          <div class="main-image">
            <img :src="product.image" :alt="product.name" />
          </div>
        </div>

        <!-- 右侧：产品信息 -->
        <div class="info-section">
          <!-- 品牌和型号 -->
          <div class="product-header">
            <div>
              <p class="brand">{{ product.brand }}</p>
              <h1 class="product-name">{{ product.name }}</h1>
              <p class="model">型号: {{ product.model }}</p>
            </div>
            <span v-if="product.tag" class="tag" :class="product.tag.toLowerCase()">
              {{ product.tag }}
            </span>
          </div>

          <!-- 规格参数 -->
          <div class="specs-section">
            <h3>产品规格</h3>
            <div class="specs-grid">
              <div v-for="spec in product.specs" :key="spec.label" class="spec-item">
                <span class="spec-label">{{ spec.label }}:</span>
                <span class="spec-value">{{ spec.value }}</span>
              </div>
            </div>
          </div>

          <!-- 操作按钮 -->
          <div class="actions">
            <button class="btn-primary" @click="handleApplySample">
              📦 申请样品
            </button>
            <button class="btn-secondary" @click="handleCommissionCalc">
              💰 佣金计算
            </button>
          </div>

          <!-- 额外操作 -->
          <div class="extra-actions">
            <button class="btn-extra" @click="handleDownloadDatasheet">
              📥 下载数据手册
            </button>
          </div>
        </div>
      </div>

      <!-- Tab 内容区 -->
      <div class="tabs-section">
        <div class="tab-content">
          <h2>产品详情</h2>
          <p class="description">{{ product.description }}</p>

          <h3>应用领域</h3>
          <ul class="applications">
            <li v-for="app in product.applications" :key="app">{{ app }}</li>
          </ul>
        </div>
      </div>
    </div>
  </div>

  <!-- 产品不存在时显示 -->
  <div v-else class="not-found">
    <div class="container">
      <h2>产品不存在</h2>
      <p>抱歉，您访问的产品不存在。</p>
      <router-link to="/products" class="btn-back">返回产品列表</router-link>
    </div>
  </div>
</template>

<style scoped>
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 140px);
  text-align: center;
}

.loading-state .spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.detail-page {
  background: var(--color-bg-page);
  padding: 40px 0 80px;
  min-height: calc(100vh - 140px);
}

.not-found {
  background: var(--color-bg-page);
  padding: 80px 0;
  text-align: center;
  min-height: calc(100vh - 140px);
}

.not-found h2 {
  font-size: 28px;
  color: var(--color-text-primary);
  margin-bottom: 12px;
}

.not-found p {
  color: var(--color-text-secondary);
  margin-bottom: 24px;
}

.btn-back {
  display: inline-block;
  padding: 12px 32px;
  background: var(--color-primary);
  color: white;
  border-radius: var(--radius-md);
  text-decoration: none;
  font-weight: 600;
}

.btn-back:hover {
  background: var(--color-primary-dark);
}

/* 面包屑 */
.breadcrumb {
  margin-bottom: 24px;
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.breadcrumb a {
  color: var(--color-primary);
  text-decoration: none;
}

.breadcrumb a:hover {
  text-decoration: underline;
}

/* 主内容区 */
.detail-content {
  display: flex;
  gap: 40px;
  margin-bottom: 40px;
  background: white;
  border-radius: var(--radius-md);
  overflow: hidden;
}

/* 左侧图片 */
.image-section {
  flex: 0 0 40%;
  padding: 20px;
  background: var(--color-bg-page);
}

.main-image {
  width: 100%;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}

.main-image img {
  width: 100%;
  height: auto;
  max-width: 100%;
  display: block;
}

/* 右侧信息 */
.info-section {
  flex: 1;
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--color-border-light);
}

.brand {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin: 0 0 8px 0;
}

.product-name {
  font-size: 28px;
  color: var(--color-text-primary);
  margin: 0 0 8px 0;
  line-height: 1.3;
  font-weight: var(--font-weight-bold);
}

.model {
  font-size: var(--font-size-base);
  color: var(--color-text-muted);
  margin: 0;
}

.tag {
  padding: 6px 12px;
  border-radius: var(--radius-sm);
  font-size: var(--font-size-xs);
  font-weight: 600;
  white-space: nowrap;
}

.tag.new {
  background: #e6f7e6;
  color: #2e7d32;
}

.tag.hot {
  background: #fff0e6;
  color: #d84315;
}

/* 规格参数 */
.specs-section {
  padding: 20px;
  background: var(--color-bg-page);
  border-radius: var(--radius-md);
}

.specs-section h3 {
  margin: 0 0 16px 0;
  font-size: var(--font-size-lg);
}

.specs-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.spec-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.spec-label {
  font-weight: 600;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.spec-value {
  color: var(--color-text-primary);
  font-size: var(--font-size-base);
}

/* 操作按钮 */
.actions {
  display: flex;
  gap: 16px;
}

.btn-primary,
.btn-secondary {
  flex: 1;
  padding: 14px 24px;
  border-radius: var(--radius-md);
  font-size: var(--font-size-lg);
  font-weight: 600;
  cursor: pointer;
  border: 2px solid;
  transition: all var(--transition-base);
}

.btn-primary {
  background: var(--color-primary);
  color: white;
  border-color: var(--color-primary);
}

.btn-primary:hover {
  background: var(--color-primary-dark);
  border-color: var(--color-primary-dark);
}

.btn-secondary {
  background: white;
  color: var(--color-primary);
  border-color: var(--color-primary);
}

.btn-secondary:hover {
  background: var(--color-primary-bg);
}

/* 额外操作 */
.extra-actions {
  display: flex;
  gap: 12px;
}

.btn-extra {
  flex: 1;
  padding: 10px 16px;
  background: white;
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-base);
}

.btn-extra:hover {
  background: var(--color-bg-page);
  color: var(--color-primary);
  border-color: var(--color-primary);
}

/* Tab 内容区 */
.tabs-section {
  background: white;
  border-radius: var(--radius-md);
  padding: 30px;
}

.tab-content h2 {
  margin: 0 0 16px 0;
  font-size: var(--font-size-2xl);
}

.tab-content h3 {
  margin: 24px 0 12px 0;
  font-size: var(--font-size-lg);
}

.description {
  line-height: 1.7;
  color: var(--color-text-secondary);
}

.applications {
  margin: 12px 0;
  padding-left: 24px;
  list-style-type: disc;
}

.applications li {
  margin-bottom: 8px;
  color: var(--color-text-secondary);
}

/* 响应式 */
@media (max-width: 768px) {
  .detail-content {
    flex-direction: column;
  }

  .image-section {
    flex: 0 0 auto;
  }

  .info-section {
    padding: 20px;
    gap: 16px;
  }

  .breadcrumb {
    font-size: var(--font-size-xs);
  }

  .product-name {
    font-size: var(--font-size-xl);
  }

  .specs-grid {
    grid-template-columns: 1fr;
  }

  .actions {
    flex-direction: column;
  }

  .tabs-section {
    padding: 20px;
  }
}
</style>
